import debug from 'shared/utils/debug';
import VideoTracking from './videoTracking';
export class VideoBase extends VideoTracking {
    constructor(videoConfig) {
        super(videoConfig);
        this.videoStarted = false; // flag to know if the video
        this.duration = 0;
        this.autoPlayCount = 1;
        this.autoPlayInProgress = false;
        this.isEnded = false;
        this.config = videoConfig;
        this.videoSelectorId = `${this.config.symbolId}-video`;
        this.isAutoplay = this.config.autoPlay === 'yes';
    }
    /**
     * Setup, load video resources
     * And, bind cover and controls' events
     */
    setup() {
        var _a, _b;
        if (!this.config.videoUrl) {
            debug('Video URL is not provided');
            return Promise.reject(this);
        }
        this.hideCoverIcon();
        if ((_b = (_a = window.ADAPT) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.low) {
            return Promise.resolve(this);
        }
        // plugins needs this video element available
        this.videoElement = document.getElementById(this.videoSelectorId);
        this.videoPlaceholder = document.getElementById(`${this.config.symbolId}-videoContent`);
        return this.pluginSetup().then(() => {
            this.assignControlElements();
            this.renderControls();
            // autoplay
            this.isAutoplay && this.autoPlay();
            return this;
        });
    }
    /**
     * Record user-defined callback
     */
    onPlaying(onPlayingCallback) {
        this.onPlayingCallback = onPlayingCallback;
    }
    onEnd(onEndCallback) {
        this.onEndCallback = onEndCallback;
    }
    onPlay(onPlayCallback) {
        this.onPlayCallback = onPlayCallback;
    }
    onStop(onStopCallback) {
        this.onStopCallback = onStopCallback;
    }
    /**
     * Playback methods
     * Play, Stop, and End requires element manipulation & tracking
     */
    play() {
        this.videoStarted = true;
        // reset isEnded;
        this.isEnded = false;
        this.pluginPlay();
        this.startTrackingTime();
        this.hideCover();
        this.renderControls();
        this.callCallback('onPlayCallback');
    }
    autoPlay() {
        this.videoStarted = true;
        this.autoPlayInProgress = true;
        this.pluginMute();
        this.play();
        this.renderControls();
        this.ensureAutoPlay();
    }
    /**
     * Ensure auto-playing is successful.
     */
    ensureAutoPlay() {
        let timer = 0;
        let tryCount = 0;
        const clear = () => window.clearInterval(timer);
        const t = () => {
            if (timer === 0) {
                return;
            }
            // autoplay succeeded. Has played pass 0
            if (this.getCurrentTime() > 0) {
                clear();
            }
            // autoplay failed. Has tried 20 times (2s)
            if (tryCount === 20) {
                clear();
                this.showCover();
                this.unMute();
                this.pause();
            }
            tryCount += 1;
        };
        timer = window.setInterval(t, 100);
    }
    replay() {
        this.play();
        this.startTrackingTimeReplay();
    }
    pause(shouldTrack = true) {
        shouldTrack && this.track('pause');
        this.pluginPause();
        this.stopTrackingTime();
        this.renderControls();
        this.callCallback('onStopCallback');
    }
    stop() {
        this.pause();
    }
    mute() {
        this.track('mute');
        this.pluginMute();
        this.renderControls();
    }
    unMute() {
        this.track('unmute');
        this.pluginUnMute();
        this.renderControls();
    }
    /**
     * process and check if video loop ended
     * - whether it has finished the loop, then return true call this.end()
     * - or it hasn't or infinite loop (loop === 0), increment autoPlayCount and play from the start
     */
    processAndCheckLoopEnded() {
        const loop = this.config.loop !== undefined ? +this.config.loop : 1;
        if (this.autoPlayCount < loop || loop === 0) {
            this.autoPlayCount += 1;
            this.replay();
            return false;
        }
        return true;
    }
    end() {
        // loop hasn't ended, do no further
        if (!this.processAndCheckLoopEnded()) {
            return;
        }
        this.isEnded = true;
        this.showCover(false);
        this.stopTrackingTime();
        this.resetPlayTracking();
        this.renderControls();
        this.callCallback('onEndCallback');
    }
    /**
     * Cover click can be play/pause
     * or it allows to click through
     */
    canClickThru() {
        return this.isEnded || this.isPlaying();
    }
    handleCoverClick() {
        // shouldn't play when video is ended (at stop state and ended)
        // users must use the controls
        if (this.isEnded) {
            return;
        }
        this.playOrPause();
    }
    /**
     * Helps cover decide to play or pause
     * in case of the ad is auto-playing,
     * it should keep playing with sound
     */
    playOrPause() {
        if (this.isPlaying()) {
            this.pause();
        }
        else {
            this.play();
        }
        this.autoPlayInProgress = false;
    }
    /**
     * Cover: always on top of the video
     * Show/Hide only a swapping of background image
     * determine image to show, prePlay or postComplete
     */
    selectCoverImage(isPre = true) {
        const { prePlayImage, postCompleteImage } = this.config;
        const postImage = postCompleteImage || prePlayImage;
        const img = isPre ? prePlayImage : postImage;
        return img || '';
    }
    hideCover() {
        this.elmCover.style.backgroundImage = '';
    }
    showCover(isPre = true) {
        const coverElm = this.elmCover;
        const coverImg = this.selectCoverImage(isPre);
        coverElm && (coverElm.style.backgroundImage = `url(${coverImg})`);
    }
    /**
     * assign & render control elements
     */
    assignControlElements() {
        const cover = document.getElementById(`${this.config.symbolId}-cover`);
        this.elmCover = cover;
        this.elmCoverIcon = cover.querySelector('.adpt-cover-icon');
        hideElm(this.elmCoverIcon);
        this.elmControls = cover.querySelector('.adpt-video-controls');
        this.elmPlay = cover.querySelector('.adpt-play');
        this.elmRePlay = cover.querySelector('.adpt-replay');
        this.elmPause = cover.querySelector('.adpt-pause');
        this.elmMute = cover.querySelector('.adpt-mute');
        this.elmUnMute = cover.querySelector('.adpt-unmute');
        // ensure its events are bindable
        this.elmCover.style.pointerEvents = 'auto';
        this.elmCover.addEventListener('click', e => {
            if (!this.canClickThru()) {
                e.stopPropagation();
            }
            this.handleCoverClick();
        });
        this.elmPlay.addEventListener('click', e => {
            e.stopPropagation();
            this.play();
        });
        this.elmRePlay.addEventListener('click', e => {
            e.stopPropagation();
            this.replay();
        });
        this.elmPause.addEventListener('click', e => {
            e.stopPropagation();
            this.pause();
        });
        this.elmMute.addEventListener('click', e => {
            e.stopPropagation();
            this.mute();
        });
        this.elmUnMute.addEventListener('click', e => {
            e.stopPropagation();
            this.unMute();
        });
    }
    renderControls() {
        if (this.config.showControls === 'no') {
            return;
        }
        hideElm(this.elmRePlay);
        this.elmControls.style.opacity = '1';
        if (this.isPlaying()) {
            showElm(this.elmPause);
            hideElm(this.elmPlay);
            this.showControls();
            this.elmControls.style.opacity = '0.5';
        }
        else {
            hideElm(this.elmPause);
            showElm(this.elmPlay);
        }
        if (this.isMuted()) {
            hideElm(this.elmMute);
            showElm(this.elmUnMute);
        }
        else {
            showElm(this.elmMute);
            hideElm(this.elmUnMute);
        }
        if (this.isEnded) {
            showElm(this.elmRePlay);
            hideElm(this.elmPlay);
            hideElm(this.elmPause);
            hideElm(this.elmMute);
            hideElm(this.elmUnMute);
            this.showControls();
            this.elmControls.style.opacity = '1';
        }
        if (this.canClickThru()) {
            hideElm(this.elmPlay);
        }
        if (!this.videoStarted && this.config.prePlayImage) {
            hideElm(this.elmControls);
        }
    }
    /**
     * Determine if it should show controls
     */
    showControls() {
        this.config.showControls !== 'no' && showElm(this.elmControls, 'flex');
    }
    callCallback(fnKey) {
        this[fnKey] && this[fnKey].call(null, this);
    }
    /**
     * Hide cover icon (only shown in app)
     */
    hideCoverIcon() {
        const cover = document.getElementById(`${this.config.symbolId}-cover`);
        hideElm(cover.querySelector('.adpt-cover-icon'));
    }
}
export default VideoBase;
const hideElm = (elm) => {
    elm.style.display = 'none';
};
const showElm = (elm, type = 'block') => {
    elm.style.display = type;
};
